import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { Descriptions, Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationCollectionOut,
  PublicationDetailOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDate } from '@/utils/format';

const { Text } = Typography;

interface DrawerGeneralTabProps {
  record: PublicationDetailOut;
}

export const DrawerGeneralTab = ({ record }: DrawerGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();

  const people = (
    items: { position: number; personId: number; name?: string }[],
    resource: string
  ): ReactNode =>
    items.length === 0 ? (
      orDash(null)
    ) : (
      <Space direction="vertical" size={2}>
        {items
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((item) => (
            <ShowIconLink
              key={item.personId}
              resource={resource}
              id={item.personId}
            >
              {item.name ?? `#${item.personId}`}
            </ShowIconLink>
          ))}
      </Space>
    );

  const tags = (
    items: PublicationCollectionOut[] | undefined,
    color?: string
  ): ReactNode =>
    items?.length ? (
      <Space size={[6, 6]} wrap>
        {items.map((item) => (
          <Tag key={item.id} color={color} style={{ marginInlineEnd: 0 }}>
            {item.name ?? `#${item.id}`}
          </Tag>
        ))}
      </Space>
    ) : (
      orDash(null)
    );

  const mainSeriesName = record.collections?.find(
    (item) => item.id === record.main_series_id
  )?.name;

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: record.id,
    },
    {
      key: 'slug',
      label: t('user_catalog.publications.fields.slug'),
      children: <Text code>{record.slug}</Text>,
    },
    {
      key: 'publisher',
      label: t('user_catalog.publications.fields.publisher'),
      children: (
        <ShowIconLink
          resource="user_catalog.publishers"
          id={record.publisher_id}
        >
          {record.publisher?.name ?? `#${record.publisher_id}`}
        </ShowIconLink>
      ),
    },
    {
      key: 'main_series',
      label: t('user_catalog.publications.fields.main_series'),
      children: record.main_series_id ? (
        <ShowIconLink
          resource="user_catalog.managed_collections"
          id={record.main_series_id}
        >
          {mainSeriesName ?? `#${record.main_series_id}`}
        </ShowIconLink>
      ) : (
        orDash(null)
      ),
    },
    {
      key: 'authors',
      label: t('user_catalog.publications.authors.title'),
      children: people(
        (record.publication_authors ?? []).map((a) => ({
          position: a.position,
          personId: a.author_id,
          name: a.author?.name,
        })),
        'user_catalog.authors'
      ),
    },
    {
      key: 'lectors',
      label: t('user_catalog.publications.lectors.title'),
      children: people(
        (record.publication_lectors ?? []).map((l) => ({
          position: l.position,
          personId: l.lector_id,
          name: l.lector?.name,
        })),
        'user_catalog.lectors'
      ),
    },
    {
      key: 'language',
      label: t('user_catalog.publications.fields.language'),
      children: orDash(record.language),
    },
    {
      key: 'audience',
      label: t('user_catalog.publications.fields.audience'),
      children: t(`user_catalog.publications.audience.${record.audience}`),
    },
    {
      key: 'duration',
      label: t('user_catalog.publications.show.fields.duration'),
      children: t(`user_catalog.publications.duration.${record.duration}`),
    },
    {
      key: 'exclusive_premiere',
      label: t('user_catalog.publications.show.fields.exclusive_premiere'),
      children: `${t(record.is_exclusive ? 'common.yes' : 'common.no')} / ${t(
        record.is_premiere ? 'common.yes' : 'common.no'
      )}`,
    },
    {
      key: 'published_at',
      label: t('user_catalog.publications.fields.published_at'),
      children: orDash(formatDate(record.published_at)),
    },
    {
      key: 'first_available_from',
      label: t('user_catalog.publications.show.fields.first_available'),
      children: orDash(formatDate(record.first_available_from)),
    },
    {
      key: 'categories',
      label: t('user_catalog.publications.fields.categories'),
      children: tags(record.active_categories ?? record.categories, 'blue'),
      span: 2,
    },
    {
      key: 'collections',
      label: t('user_catalog.publications.fields.collections'),
      children: tags(record.active_collections ?? record.collections, 'purple'),
      span: 2,
    },
    {
      key: 'tags',
      label: t('user_catalog.publications.fields.tags'),
      children: tags(record.active_custom_tags ?? record.custom_tags),
      span: 2,
    },
  ];

  return <Descriptions {...verticalDescProps} column={2} items={items} />;
};
